import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { EmptyState } from "../UI/States";

const ThumbnailRow = ({ items, onEdit, onDelete }) => {
  if (!items.length) {
    return (
      <EmptyState
        compact
        icon={ArticleOutlinedIcon}
        title="No blog posts yet"
        description="Posts you create above show up here."
      />
    );
  }
  return (
    <div className="-mx-1 overflow-x-auto">
      <div className="flex min-w-max gap-3 p-1">
        {items.map((item) => (
          <div
            key={item._id}
            className="app-card flex w-44 flex-col items-center justify-between rounded-2xl border p-3 sm:w-52"
          >
            <img
              src={`https://node.bigclumber.com:8449/web/blog/displayThumbnail/${item.thumbnailUrl.replace(
                "/thumbnails/",
                ""
              )}`}
              alt={item.title}
              className="aspect-square w-full rounded-xl bg-slate-500/10 object-cover"
            />
            <h3 className="mt-2 line-clamp-2 text-center text-sm font-semibold">
              {item.title}
            </h3>
            <div className="mt-3 flex w-full flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => onEdit(item)}
                className="app-btn app-btn-secondary app-btn-sm flex-1"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => onDelete(item._id)}
                className="app-btn app-btn-sm flex-1 border-rose-500/40 text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BlogEditor = () => {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  const [category, setCategory] = useState("");
  const [id, setID] = useState("");
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState("");

  const [allBlogs, setAllBlogs] = useState([]);

  const handleEdit = (blog) => {
    setStatus("");
    setEditing(true);
    setTitle(blog.title);
    setSummary(blog.summary);
    setContent(blog.content);
    setCategory(blog.category);
    setID(blog._id);
  };

  const discardEdit = () => {
    setEditing(false);
    setTitle("");
    setSummary("");
    setContent("");
    setCategory("");
    setID("");
    setStatus("");
  };

  const handleDelete = async (id) => {
    const formData = new FormData();
    formData.append("id", id);

    try {
      const response = await fetch("/api/cloud/website/deleteBlog", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setStatus("Blog deleted successfully");
        getAllBlogs();
      } else {
        setStatus("Failed to delete blog");
      }
    } catch (error) {
      console.error("Error deleting blog:", error);
      setStatus("An error occurred while deleting the blog");
    }
  };

  const getAllBlogs = async () => {
    return fetch(`/api/cloud/website/getAllBlogs`)
      .then((response) => response.json())
      .then((responseJson) => {
        console.log(responseJson);
        setAllBlogs(responseJson);
      })
      .catch((error) => {
        //console.error(error);
      });
  };

  useEffect(() => {
    getAllBlogs();
  }, []);

  const handleBlogUpdate = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("summary", summary);
    formData.append("category", category);
    formData.append("id", id);

    try {
      const response = await fetch("/api/cloud/website/updateBlog", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setStatus("Blog updated successfully");
        getAllBlogs();
        setTitle("");
        setContent("");
        setSummary("");
        setImage(null);
        setCategory("");
        setID("");
        setEditing(false);
      } else {
        setStatus("Failed to update blog");
      }
    } catch (error) {
      console.error("Error updating blog:", error);
      setStatus("An error occurred while updating the blog");
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("thumbnail", image);
    formData.append("summary", summary);
    formData.append("category", category);

    try {
      const response = await fetch("/api/cloud/website/createBlog", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setStatus("Blog created successfully");
        getAllBlogs();
        setTitle("");
        setContent("");
        setSummary("");
        setImage(null);
        setCategory("");
      } else {
        setStatus("Failed to create blog");
      }
    } catch (error) {
      console.error("Error creating blog:", error);
      setStatus("An error occurred while creating the blog");
    }
  };

  //const toolbarOptions -> ['First','Second','Third','...nth']
  //{{header: [1,2,3,4,5]}}

  const toolbarOptions = [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ direction: "rtl" }],
    [{ align: [] }],
    ["link", "image", "video"],
    ["blockquote", "code-block"],
    ["clean"],
  ];
  const fieldClass =
    "app-input w-full rounded-xl border px-3 py-2 text-sm";

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Create a blog post</h2>
        <p className="app-muted max-w-3xl text-sm">
          Draft a headline, add a short summary, and write the full story.
          Upload a square thumbnail to highlight the post across the site.
        </p>
      </div>

      {status && (
        <div
          role="status"
          className="rounded-xl bg-sky-500/15 px-3 py-2 text-sm text-sky-800 dark:text-sky-200"
        >
          {status}
        </div>
      )}

      <div className="app-surface rounded-xl border p-4">
        <form onSubmit={handleBlogSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="blog-title" className="text-sm font-medium">
                Blog Title
              </label>
              <input
                id="blog-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Bathroom remodel trends for 2024"
                required
                className={fieldClass}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="category" className="text-sm font-medium">
                Category
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                className={fieldClass}
              >
                <option value="" disabled>
                  Select a category
                </option>
                <option value="KitchensBathrooms">Kitchens & Bathrooms</option>
                <option value="Cabinets">Cabinets</option>
                <option value="Roofing">Roofing</option>
                <option value="HeatingCooling">Heating & Cooling</option>
                <option value="WindowsDoors">Windows & Doors</option>
                <option value="Insulation">Insulation</option>
                <option value="Decking">Decking</option>
                <option value="Siding">Siding</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="blog-summary" className="text-sm font-medium">
              Short summary
            </label>
            <input
              id="blog-summary"
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Two to three sentences that will show on cards"
              required
              className={fieldClass}
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Blog content</span>
            <ReactQuill
              value={content}
              onChange={setContent}
              placeholder="Write your blog content here..."
              className="rounded-xl bg-[color:var(--app-input-bg)] text-[color:var(--app-input-text)]"
              modules={{ toolbar: toolbarOptions }}
            />
          </div>

          {!editing ? (
            <div className="flex flex-col gap-4 border-t app-divider pt-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex flex-col gap-1">
                <label htmlFor="blog-thumbnail" className="text-sm font-medium">
                  Blog Thumbnail
                </label>
                <input
                  id="blog-thumbnail"
                  type="file"
                  onChange={(e) => setImage(e.target.files[0])}
                  accept="image/*"
                  required
                  className="app-input w-full rounded-xl border p-2 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-sky-500/15 file:px-3 file:py-1 file:text-sm file:font-semibold file:text-sky-700 dark:file:text-sky-300 sm:w-96"
                />
                <p className="app-muted text-xs">Square images (1:1) work best.</p>
              </div>

              <button className="app-btn app-btn-primary" type="submit">
                Create Blog
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 border-t app-divider pt-4">
              <button
                className="app-btn app-btn-primary"
                onClick={(e) => {
                  handleBlogUpdate(e);
                }}
              >
                Update Blog
              </button>
              <button
                className="app-btn app-btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  discardEdit();
                }}
              >
                Cancel Edit
              </button>
            </div>
          )}
        </form>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">All Blogs</h2>
          <p className="app-muted text-sm">Tap a card to edit or delete.</p>
        </div>
        <ThumbnailRow items={allBlogs} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </div>
  );
};

export default BlogEditor;
